// filepath: apps/web/src/renderers/form/fields/BooleanField.tsx
//
// Checkbox input. Stores a boolean.

import React from "react";
import type { FieldComponentProps } from "../FieldRegistry";
import { getUiSchemaField } from "../FieldRegistry";
import { useTranslation } from "@/i18n/useTranslation";
import { resolveLocalizedValue } from "@/i18n/localeMap";
import { useIsMobile, MIN_TOUCH_TARGET_PX } from "@/runtime/useIsMobile";

export function BooleanField({
  fieldId,
  value,
  onChange,
  disabled,
  uiSchema,
  fieldError,
}: FieldComponentProps): React.ReactElement {
  const { locale } = useTranslation();
  const isMobile = useIsMobile();
  const uiField = getUiSchemaField(uiSchema, fieldId);
  const labelText = resolveLocalizedValue(uiField?.label, locale, fieldId);

  // FR-UI-NFR-3 AC-3: 48×48dp touch target; AC-5: no text truncation.
  const labelStyle: React.CSSProperties = isMobile
    ? {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        fontWeight: 500,
        minHeight: `${MIN_TOUCH_TARGET_PX}px`,
        justifyContent: "center",
      }
    : { fontWeight: 500 };
  const checkboxStyle: React.CSSProperties = isMobile
    ? { width: "24px", height: "24px", cursor: "pointer" }
    : { cursor: "pointer" };

  return (
    <div data-testid={`field-${fieldId}`} className="field field-boolean">
      <label htmlFor={fieldId} style={labelStyle}>
        <input
          id={fieldId}
          type="checkbox"
          name={fieldId}
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          aria-required={uiField?.required ? "true" : undefined}
          aria-invalid={fieldError ? "true" : undefined}
          aria-describedby={fieldError?.id}
          data-testid={`input-${fieldId}`}
          style={checkboxStyle}
        />{" "}
        {labelText}
        {uiField?.required ? <span aria-hidden="true"> *</span> : null}
      </label>
    </div>
  );
}
